import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {useSelector} from '../../reduxHooks';
import {Alert, Button, Dialog} from '@betareader/design-system';
import AppIcon from '../../components/AppIcon';
import styled from 'styled-components';
import {
  CritiqueGroup,
  CritiqueJoinRequest,
  CritiqueMember,
  useRemoveMember,
  useResolveJoinRequest,
  useUpdateMemberRole,
} from 'src/hooks/useCritiqueGroups';
import {
  activeMembers,
  isHost as isHostOf,
  userRefId,
  userRefName,
} from 'src/utils/critiqueGroupUtil';
import { CritiqueAvatar } from 'src/components/critiqueGroups/critiqueAvatar';
import { MemberName } from 'src/components/critiqueGroups/memberName';
import { CritiqueEmptyState } from 'src/components/critiqueGroups/critiqueStates';
import { MediaQuery } from 'src/constants/size';

/**
 * Design C2 + C3 — the roster, join requests, and the access-consequence
 * dialogs. Blocking or removing a member REVOKES their read access to the
 * linked book(s) server-side, so the confirm dialogs lead with that: it's the
 * part a host won't otherwise predict, and it isn't undoable from here.
 */

const Panel = styled.div`
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 14px;
  overflow: hidden;
`;

const Head = styled.div`
  padding: 18px 22px;
  border-bottom: 1px solid #eee7da;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  ${MediaQuery.phone} {
    padding: 14px;
  }
`;

const Pending = styled.div`
  padding: 14px 16px;
  background: var(--br-raised-surface);
  border-bottom: 1px solid #f4e6d2;
`;

const PendingLabel = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #c85f14;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Row = styled.div<{ $alt?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${({ $alt }) => ($alt ? '#fbfaf6' : 'transparent')};
  ${MediaQuery.phone} {
    padding: 12px 8px;
  }
`;

const RoleTag = styled.span<{ $tone: 'host' | 'mod' | 'plain' | 'blocked' }>`
  font-size: 10.5px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
  color: ${({ $tone }) =>
    $tone === 'host'
      ? '#00817b'
      : $tone === 'blocked'
      ? '#a23a3a'
      : $tone === 'mod'
      ? '#8a857b'
      : '#a49f93'};
  background: ${({ $tone }) =>
    $tone === 'host'
      ? '#e3f6f4'
      : $tone === 'blocked'
      ? '#fbe9e9'
      : $tone === 'mod'
      ? '#f0ece3'
      : 'transparent'};
`;

const Consequence = styled.div`
  margin-top: 14px;
  background: var(--br-raised-surface);
  border: 1px solid #f2d5d5;
  border-radius: 10px;
  padding: 11px 13px;
  font-size: 12.5px;
  color: #a23a3a;
  display: flex;
  gap: 9px;
  align-items: flex-start;
`;

const MemberMenu = styled.details`
  position: relative;
  summary { cursor: pointer; list-style: none; padding: 0.5rem; }
  summary::-webkit-details-marker { display: none; }
  &[open] > div {
    position: absolute;
    right: 0;
    top: 100%;
    z-index: 10;
    display: grid;
    min-width: 190px;
    padding: 0.4rem;
    background: var(--br-surface);
    border: 1px solid var(--br-border);
    border-radius: var(--br-control-radius);
  }
  button { text-align: left; }
`;

type PendingAction =
  | { kind: 'remove'; member: CritiqueMember }
  | { kind: 'block'; member: CritiqueMember }
  | null;

type Props = {
  group: CritiqueGroup;
  requests: { data?: CritiqueJoinRequest[]; isLoading: boolean };
};

const HostMembers: React.FC<Props> = ({ group, requests }) => {
  const { t } = useTranslation();
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const updateRole = useUpdateMemberRole(group._id);
  const removeMember = useRemoveMember(group._id);
  const resolve = useResolveJoinRequest(group._id);

  const [confirm, setConfirm] = useState<PendingAction>(null);
  const [actionError, setActionError] = useState<string | undefined>();

  const iAmHost = isHostOf(group, userProfile?._id);
  const members = activeMembers(group);
  const blocked = (group.members || []).filter(m => m.role === 'blocked');
  const pending = requests.data || [];

  const bookTitles = (group.books || [])
    .map(b => (typeof b === 'string' ? undefined : b?.title))
    .filter(Boolean) as string[];
  const bookLabel =
    bookTitles.length > 0
      ? bookTitles.join(', ')
      : t('CritiqueTheBook', 'the linked book');

  const runAction = () => {
    if (!confirm) return;
    setActionError(undefined);
    const userId = userRefId(confirm.member.user);
    const onError = (err: any) => setActionError(err?.message);
    const onSuccess = () => setConfirm(null);

    if (confirm.kind === 'remove') {
      removeMember.mutate({ userId }, { onSuccess, onError });
    } else {
      updateRole.mutate({ userId, role: 'blocked' }, { onSuccess, onError });
    }
  };

  const memberName = (m: CritiqueMember) => userRefName(m.user, t);

  const busy = updateRole.isPending || removeMember.isPending;
  const closeMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.closest('details')?.removeAttribute('open');
  };

  return (
    <div data-testid="host-members">
      <Panel>
        <Head>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
            {t('CritiqueMembers', 'Members')}
          </h3>
          <span style={{ fontSize: 12.5, color: '#8a857b' }}>
            {group.memberCap
              ? t('CritiqueSeatsUsed', '{{used}} of {{cap}} seats used', {
                  used: members.length,
                  cap: group.memberCap,
                })
              : t('CritiqueNMembers', '{{count}} members', { count: members.length })}
          </span>
        </Head>

        {pending.length > 0 && (
          <Pending data-testid="members-pending">
            <PendingLabel>
              {t('CritiquePendingN', 'Pending · {{count}}', { count: pending.length })}
            </PendingLabel>
            {pending.map(r => (
              <div
                key={r._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 8,
                  flexWrap: 'wrap',
                }}
              >
                <CritiqueAvatar id={userRefId(r.user)} name={userRefName(r.user)} />
                <MemberName style={{ fontSize: 13.5 }}>
                  {userRefName(r.user, t)}
                </MemberName>
                <Button
                  size="small"
                  style={{ borderRadius: 7 }}
                  aria-busy={resolve.isPending}
                  disabled={resolve.isPending}
                  onClick={() => resolve.mutate({ requestId: r._id, action: 'approve' })}
                  data-testid={`members-approve-${r._id}`}
                >
                  {t('Approve', 'Approve')}
                </Button>
                <Button
                  size="small"
                  variant='secondary'
                  style={{ borderRadius: 7 }}
                  disabled={resolve.isPending}
                  onClick={() => resolve.mutate({ requestId: r._id, action: 'decline' })}
                >
                  {t('Decline', 'Decline')}
                </Button>
              </div>
            ))}
          </Pending>
        )}

        <div style={{ padding: '6px 8px' }}>
          <Row>
            <CritiqueAvatar
              id={group.createdBy}
              name={userProfile?.displayName}
              size={34}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {userProfile?.displayName || t('You', 'You')}{' '}
                <span style={{ color: '#a49f93', fontWeight: 400 }}>
                  ({t('you', 'you')})
                </span>
              </div>
            </div>
            <RoleTag $tone="host">{t('CritiqueAdminHost', 'Admin · Host')}</RoleTag>
          </Row>

          {members.length === 0 && pending.length === 0 && (
            <CritiqueEmptyState
              data-testid="members-empty"
              icon="user plus"
              title={t('CritiqueNoMembersTitle', 'Invite your first readers')}
              body={t(
                'CritiqueNoMembersBody',
                'Share an invite link or pick from readers who’ve followed your book.'
              )}
              footnote={
                group.memberCap
                  ? t('CritiqueCapFootnote', 'Up to {{count}} members on this plan', {
                      count: group.memberCap,
                    })
                  : undefined
              }
            />
          )}

          {members.map((m, i) => (
            <Row key={m._id} $alt={i % 2 === 0} data-testid={`member-row-${userRefId(m.user)}`}>
              <CritiqueAvatar id={userRefId(m.user)} name={userRefName(m.user)} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {memberName(m)}
                </div>
              </div>
              <RoleTag $tone={m.role === 'moderator' ? 'mod' : 'plain'}>
                {m.role === 'moderator'
                  ? t('CritiqueModerator', 'Moderator')
                  : t('CritiqueMember', 'Member')}
              </RoleTag>
              <MemberMenu
                data-testid={`member-menu-${userRefId(m.user)}`}
                onKeyDown={event => {
                  if (event.key === 'Escape') event.currentTarget.removeAttribute('open');
                }}
              >
                <summary aria-label={`${t('Actions')} ${memberName(m)}`}
                  aria-disabled={busy}
                  onClick={event => { if (busy) event.preventDefault(); }}>⋮</summary>
                <div>
                  {/* Only the host may manage moderators — a moderator gets a
                      403, so don't offer it to them. */}
                  {iAmHost && (
                    <Button variant='ghost' size='small' disabled={busy}
                      onClick={event => {
                        closeMenu(event);
                        updateRole.mutate({
                          userId: userRefId(m.user),
                          role: m.role === 'moderator' ? 'member' : 'moderator',
                        });
                      }}
                    >
                      {m.role === 'moderator'
                        ? t('CritiqueDemoteMod', 'Remove moderator')
                        : t('CritiquePromoteMod', 'Promote to moderator')}
                    </Button>
                  )}
                  <Button variant='ghost' size='small' disabled={busy}
                    onClick={event => {
                      closeMenu(event);
                      setConfirm({kind: 'block', member: m});
                    }}>
                    {t('CritiqueBlock', 'Block from group')}
                  </Button>
                  <Button variant='ghost' size='small' disabled={busy}
                    onClick={event => {
                      closeMenu(event);
                      setConfirm({kind: 'remove', member: m});
                    }}
                    data-testid={`member-remove-${userRefId(m.user)}`}
                  >
                    {t('CritiqueRemoveMember', 'Remove member')}
                  </Button>
                </div>
              </MemberMenu>
            </Row>
          ))}

          {blocked.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: '#a23a3a',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                  margin: '14px 6px 4px',
                }}
              >
                {t('CritiqueBlockedN', 'Blocked · {{count}}', { count: blocked.length })}
              </div>
              {blocked.map(m => (
                <Row key={m._id} data-testid={`blocked-row-${userRefId(m.user)}`}>
                  <CritiqueAvatar
                    id={userRefId(m.user)}
                    name={userRefName(m.user)}
                    size={34}
                    muted
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#8a857b',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {memberName(m)}
                    </div>
                    <div style={{ fontSize: 12, color: '#a49f93' }}>
                      {t('CritiqueBlockedNoAccess', 'No access to the book')}
                    </div>
                  </div>
                  <RoleTag $tone="blocked">{t('CritiqueBlocked', 'Blocked')}</RoleTag>
                  {/* Unblocking is host-only server-side and re-consumes a seat
                      (it can 403 with MEMBER_CAP_REACHED). */}
                  {iAmHost && (
                    <Button
                      size="small"
                      variant='secondary'
                      style={{ borderRadius: 7 }}
                      aria-busy={updateRole.isPending}
                      disabled={busy}
                      onClick={() =>
                        updateRole.mutate(
                          { userId: userRefId(m.user), role: 'member' },
                          { onError: (err: any) => setActionError(err?.message) }
                        )
                      }
                      data-testid={`unblock-${userRefId(m.user)}`}
                    >
                      {t('CritiqueUnblock', 'Unblock')}
                    </Button>
                  )}
                </Row>
              ))}
            </>
          )}
        </div>
      </Panel>

      {actionError && !confirm && (
        <Alert variant='error' style={{ marginTop: 12 }} data-testid="members-error">
          {actionError}
        </Alert>
      )}

      <Dialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        role='alertdialog'
        title={
          confirm?.kind === 'remove'
            ? t('CritiqueRemoveQ', 'Remove {{name}} from the group?', {
                name: confirm ? memberName(confirm.member) : '',
              })
            : t('CritiqueBlockQ', 'Block {{name}} from the group?', {
                name: confirm ? memberName(confirm.member) : '',
              })
        }
        actions={<>
          <Button variant='secondary' onClick={() => setConfirm(null)}
            style={{ borderRadius: 9 }}>
            {t('Cancel', 'Cancel')}
          </Button>
          <Button variant='danger' onClick={runAction} aria-busy={busy}
            disabled={busy} style={{ borderRadius: 9, fontWeight: 700 }}
            data-testid="member-consequence-confirm">
            {confirm?.kind === 'remove'
              ? t('CritiqueRemoveRevoke', 'Remove & revoke access')
              : t('CritiqueBlockRevoke', 'Block & revoke access')}
          </Button>
        </>}>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: '#4a4740' }}>
            {/* Trans keeps the bolded consequence inline and translatable. */}
            <Trans
              i18nKey={
                confirm?.kind === 'remove'
                  ? 'CritiqueRemoveBody'
                  : 'CritiqueBlockBody'
              }
              values={{
                name: confirm ? memberName(confirm.member) : '',
                book: bookLabel,
              }}
              defaults={
                confirm?.kind === 'remove'
                  ? 'This also <1>removes {{name}}’s access to {{book}}.</1> Their feedback and comments so far stay with you, but they’ll no longer be able to read or contribute.'
                  : 'This also <1>removes {{name}}’s access to {{book}}.</1> Their feedback stays with you. You can unblock them later — they’ll need a free seat.'
              }
              components={{ 1: <strong /> }}
            />
          </p>
          {confirm?.kind === 'remove' && (
            <Consequence>
              <AppIcon name="info circle" aria-hidden='true' />
              {t(
                'CritiqueRemoveIrreversible',
                'This can’t be undone — you’d need to re-invite them.'
              )}
            </Consequence>
          )}
          {actionError && (
            <Alert variant='error' style={{ marginTop: 12 }}>
              {actionError}
            </Alert>
          )}
      </Dialog>
    </div>
  );
};

export default HostMembers;
